/** Shared fetch wrapper for every `/api/*` client.
 *
 * Every API file (strategies, broker, daily-config, auth) used to redefine
 * the same fetch-and-check-status helpers. jscpd flagged it as ~9% TS
 * duplication. This is the single source of truth.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API error ${String(status)}`);
    this.name = "ApiError";
  }
}

/** Turn a thrown API error into something worth putting in a toast.
 *
 * FastAPI puts the reason in `detail`, and `ApiError` already carries the
 * parsed body — but callers used to render the bare status, so a refusal
 * surfaced as "HTTP 409" and a fault as "HTTP 500", leaving the operator to
 * go read the server logs to find out what the server actually said. That
 * is how the broken strategy-delete cascade presented for a day: the UI
 * could only report "Delete failed: HTTP 500".
 *
 * Falls back to the status when there is no string `detail` — a validation
 * error's `detail` is an array, and those are for the form to render, not
 * for a toast.
 */
export function describeError(err: unknown): string {
  if (!(err instanceof ApiError)) return "network";
  return detailOf(err.body) ?? `HTTP ${String(err.status)}`;
}

function detailOf(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const { detail } = body as { detail?: unknown };
  return typeof detail === "string" ? detail : null;
}

export async function fetchOrThrow(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(path, {
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    ...init,
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  return res;
}

export async function getJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchOrThrow(path, init);
  return res.json() as Promise<T>;
}

export async function fireAndForget(path: string, init: RequestInit = {}): Promise<void> {
  await fetchOrThrow(path, init);
}
